import noDataImg from "../../../assets/img/no-data.png"
import { CompactTable } from "@table-library/react-table-library/compact"
import classNames from "classnames"
import { SelectPicker } from "rsuite"
import React, { useEffect, useState } from "react"
import useTranslate from "../../../hooks/useTranslate"
import { formatDate } from "../../../utils/datetime"
import {
  barChartConfig,
  GroupingType,
  lineChartConfigWithThreshold,
  numberComparison,
  thReachedColor, thTopColor
} from "./PatientGameStatsCommon"
import { usePagination } from "@table-library/react-table-library/pagination"
import { useTheme } from "@table-library/react-table-library/theme"
import { getTheme } from "@table-library/react-table-library/mantine"
import { StroopPlayedGameResultDto } from "../../../sdk"
import { Chart } from "chart.js/auto"
import dayjs from "dayjs"

type PlayedGameData = StroopPlayedGameResultDto & { id: number }

interface StroopGameStatsProps {
  fullData: PlayedGameData[]
  filteredData: PlayedGameData[]
}

export function StroopGameStats({fullData, filteredData}: StroopGameStatsProps) {
  const translateLocalazy = useTranslate()
  const theme = useTheme(getTheme())
  const [expandedIds, setExpandedIds] = useState<number[]>([])
  const [logarithmic, setLogarithmic] = useState(false)
  const [grouping, setGrouping] = useState<GroupingType | null>(null)

  useEffect(() => {
    const charts: Chart[] = []

    let playedGames = [...filteredData]
      .sort((a, b) => dayjs(a.playedAt).diff(b.playedAt, 'seconds'))

    if (grouping != null) {
      const grouped = playedGames.reduce((acc, game) => {
        const key = dayjs(game.playedAt).startOf(grouping).toISOString()
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(game)
        return acc
      }, {} as Record<string, PlayedGameData[]>)

      playedGames = Object.entries(grouped).map(([date, e], i) => ({
        id: i,
        playedAt: date,
        points: Math.round(e.reduce((acc, game) => acc + game.points, 0) / e.length),
        averageAnswerTimeMilliseconds: Math.round(e.reduce((acc, game) => acc + game.averageAnswerTimeMilliseconds, 0) / e.length),
        successPercent: Math.round(e.reduce((acc, game) => acc + game.successPercent, 0) / e.length),
        answerTimeMilliseconds: [],
        gameLengthSeconds: 0
      }))
        .sort((a, b) => dayjs(a.playedAt).diff(b.playedAt, 'seconds'))
    }

    const successRateTh = 85
    const successRateData = {
      labels: playedGames.map(e => dayjs(e.playedAt).format('MM/DD')),
      datasets: [
        {
          label: translateLocalazy('patient.game-stats.chart.accuracy.title'),
          data: playedGames.map(e => e.successPercent),
          borderColor: '#8fbc6b',
          backgroundColor: 'rgba(82, 158, 0, 0.15)',
          fill: true,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
          pointBackgroundColor: (ctx) =>
            ctx.parsed?.y < successRateTh ? thReachedColor : thTopColor,
          pointBorderColor: (ctx) =>
            ctx.parsed?.y < successRateTh ? thReachedColor : thTopColor,
        },
      ]
    }
    const pointsTh = 30
    const pointsData = {
      labels: playedGames.map(e => dayjs(e.playedAt).format('MM/DD')),
      datasets: [
        {
          label: translateLocalazy('patient.game-stats.chart.points.title'),
          data: playedGames.map(e => e.points),
          borderColor: '#8fbc6b',
          backgroundColor: 'rgba(82, 158, 0, 0.15)',
          fill: true,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
          pointBackgroundColor: (ctx) =>
            ctx.parsed?.y < pointsTh ? thReachedColor : thTopColor,
          pointBorderColor: (ctx) =>
            ctx.parsed?.y < pointsTh ? thReachedColor : thTopColor,
        },
      ]
    }
    const tapSpeedTh = 3500
    const tapSpeedData = {
      labels: playedGames.map(e => dayjs(e.playedAt).format('MM/DD')),
      datasets: [
        {
          label: translateLocalazy('patient.game-stats.chart.answer-delay.title'),
          data: playedGames.map(e => e.averageAnswerTimeMilliseconds),
          borderColor: '#8fbc6b',
          backgroundColor: 'rgba(82, 158, 0, 0.15)',
          fill: true,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
          pointBackgroundColor: (ctx) =>
            ctx.parsed?.y > tapSpeedTh ? thReachedColor : thTopColor,
          pointBorderColor: (ctx) =>
            ctx.parsed?.y > tapSpeedTh ? thReachedColor : thTopColor,
        },
      ]
    }

    const successRateChartCtx = document.getElementById(`success-rate-chart-stroop`)
    if (successRateChartCtx) {
      // @ts-ignore
      charts.push(new Chart(successRateChartCtx, {
        ...lineChartConfigWithThreshold(successRateTh, `${successRateTh}%`),
        data: successRateData
      }))
    }
    const pointsChartCtx = document.getElementById(`points-chart-stroop`)
    if (pointsChartCtx) {
      // @ts-ignore
      charts.push(new Chart(pointsChartCtx, {
        ...lineChartConfigWithThreshold(pointsTh, `${pointsTh}`),
        data: pointsData
      }))
    }
    const tapSpeedChartCtx = document.getElementById(`tap-speed-chart-stroop`)
    if (tapSpeedChartCtx) {
      // @ts-ignore
      charts.push(new Chart(tapSpeedChartCtx, {
        ...lineChartConfigWithThreshold(tapSpeedTh, `${(tapSpeedTh / 1000).toFixed(1)}s`),
        data: tapSpeedData
      }))
    }

    return () => {
      charts.map(c => c.destroy())
    }
  }, [filteredData, grouping])

  useEffect(() => {
    const charts: Chart[] = []
    for (const id of expandedIds) {
      const gameData = fullData[id - 1].answerTimeMilliseconds ?? []
      const chartData = {
        labels: gameData.map(() => ``),
        datasets: [
          {
            label: translateLocalazy('patient.game-stats.chart.game-answer-delays.title'),
            data: gameData,
            barPercentage: 0.85,
            borderColor: '#8fbc6b',
            hoverBorderColor: '#8fbc6b',
            backgroundColor: '#8fbc6b',
            hoverBackgroundColor: '#72ab42',
            borderWidth: 2,
            borderRadius: {
              topLeft: 5,
              topRight: 5
            },
          }
        ]
      }

      const ctx = document.getElementById(`tap-speed-chart-${id}-stroop`)
      const config = barChartConfig

      // @ts-ignore
      config.options.scales.y.type = logarithmic ? 'logarithmic' : undefined

      // @ts-ignore
      const chart = new Chart(ctx, {...config, data: chartData})
      charts.push(chart)
    }

    return () => {
      charts.map(c => c.destroy())
    }
  }, [expandedIds, logarithmic])

  const handleExpand = (item: PlayedGameData) => {
    if (expandedIds.includes(item.id)) {
      setExpandedIds(expandedIds.filter((id) => id !== item.id))
    } else {
      setExpandedIds(expandedIds.concat(item.id))
    }
  }

  const rowProps = {
    onClick: handleExpand,
  }
  const rowOptions = {
    renderAfterRow: (item: PlayedGameData) => (
      <>
        {expandedIds.includes(item.id) && <tr style={{display: "flex", gridColumn: "1 / -1", position: 'relative'}}>
            <button className={classNames('g_btn g_btn--small log-button', {
              'g_btn--primary': logarithmic,
              'g_btn--outlined': !logarithmic
            })}
                    type="button"
                    onClick={() => setLogarithmic(!logarithmic)}>log ()
            </button>
            <canvas id={`tap-speed-chart-${item.id}-stroop`}></canvas>
        </tr>}
      </>
    ),
  }

  const tableColumns = [
    {label: '#', renderCell: (item) => fullData.length - item.id + 1},
    {
      label: translateLocalazy('patient.game-stats.chart.points.title'), renderCell: (item) => {
        const prevItem = item.id === fullData.length ? null : fullData[item.id]

        return numberComparison(prevItem?.points, item.points)
      }
    },
    {
      label: translateLocalazy('patient.game-stats.chart.accuracy.title'), renderCell: (item) => {
        const prevItem = item.id === fullData.length ? null : fullData[item.id]

        return numberComparison(prevItem?.successPercent, item.successPercent)
      }
    },
    {
      label: translateLocalazy('patient.game-stats.chart.answer-delay.title'),
      renderCell: (item) => {
        const prevItem = item.id === fullData.length ? null : fullData[item.id]

        return numberComparison(prevItem?.averageAnswerTimeMilliseconds, item.averageAnswerTimeMilliseconds, 'ms', true)
      },
    },
    {
      label: translateLocalazy('patient.game-stats.played-at'),
      renderCell: (item) => formatDate(item.playedAt),
    },
  ]

  const pagination = usePagination({nodes: filteredData}, {
    state: {
      page: 0,
      size: 10,
    },
  })

  return <>
    {filteredData.length == 0 &&
        <div className="card-box no-data flex flex-column">
            <img src={noDataImg}/>
            <div className="no-data-text">{fullData.length === 0 ?
              translateLocalazy('patient.game-stats.no-data') :
              translateLocalazy('patient.game-stats.no-data-for-period')}</div>
        </div>
    }

    {filteredData.length > 0 && <div className="card-box">
        <div>
            <CompactTable
                columns={tableColumns}
                data={{nodes: filteredData}}
                theme={theme}
                rowProps={rowProps}
                rowOptions={rowOptions}
                pagination={pagination}
            />
        </div>
        <br/>
        <div className="pagination">
            <span>Total Pages: {pagination.state.getTotalPages(filteredData)}</span>

            <span>
          {translateLocalazy('patient.game-stats.page')}&nbsp;

              {pagination.state.getPages(filteredData).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={classNames({
                    'active': pagination.state.page === i
                  })}
                  onClick={() => pagination.fns.onSetPage(i)}
                >
                  {i + 1}
                </button>
              ))}
        </span>
        </div>
    </div>}
    <div className="flex justify-end margin-me--btm">
      <SelectPicker
        name="group-by"
        menuAutoWidth={true}
        onChange={setGrouping}
        data={[
          {label: translateLocalazy('patient.game-stats.group-by-day'), value: GroupingType.day},
          {label: translateLocalazy('patient.game-stats.group-by-week'), value: GroupingType.week},
          {
            label: translateLocalazy('patient.game-stats.group-by-month'),
            value: GroupingType.month
          }]}
        placeholder={translateLocalazy('patient.game-stats.group-by-placeholder')}
      />
    </div>
    <div className="card-box">
      <canvas id="success-rate-chart-stroop"></canvas>
    </div>
    <div className="card-box">
      <canvas id="points-chart-stroop"></canvas>
    </div>
    <div className="card-box">
      <canvas id="tap-speed-chart-stroop"></canvas>
    </div>
  </>
}
